import { apiRequest } from "../../utils/api";
import {
  Research,
  ResearchDetail,
  ResearchCreateRequest,
  ResearchStats,
  ExportRequest,
  ExportResponse,
  StreamingMessage,
  EditPlanRequest,
  EditPlanResponse,
} from "./types";

const BASE_URL = "/api/v1/deep-research";

// CSRFトークンを取得するヘルパー関数
const getCSRFToken = (): string => {
  const csrfToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken="))
    ?.split("=")[1];
  return csrfToken || "";
};

export const deepResearchApi = {
  // 調査開始（非ストリーミング）
  startResearch: async (request: ResearchCreateRequest) => {
    return apiRequest<{
      success: boolean;
      research_uuid: string;
      plan_text?: string;
      status?: string;
      message?: string;
      error?: string;
    }>(`${BASE_URL}/start/`, "POST", request);
  },

  // 計画承認して調査継続（ストリーミング）
  continueResearchWithApprovedPlan: async (
    uuid: string,
    approvedPlan: string | undefined,
    onMessage: (message: StreamingMessage) => void,
    onError: (error: Error) => void,
    onComplete: () => void
  ): Promise<void> => {
    try {
      const csrfToken = getCSRFToken();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (csrfToken) {
        headers["X-CSRFToken"] = csrfToken;
      }

      const requestBody = approvedPlan ? { plan_text: approvedPlan } : {};

      const response = await fetch(`${BASE_URL}/${uuid}/approve-plan/`, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
        credentials: "include", // Cookieを含める
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            onComplete();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.trim()) {
              // browsing形式のJSON直接パース（data: プレフィックスなし）
              const parsedData = JSON.parse(line.trim());

              // browsing形式の場合、dataプロパティ内にメッセージ情報がある
              if (parsedData.data) {
                const message: StreamingMessage = {
                  type: parsedData.data.type,
                  status: parsedData.data.status,
                  message: parsedData.data.message,
                  query: parsedData.data.query,
                  progress: parsedData.data.progress,
                  plan_text: parsedData.data.plan_text,
                  current_section: parsedData.data.current_section,
                  search_count: parsedData.data.search_count,
                  max_searches: parsedData.data.max_searches,
                  research_mode: parsedData.data.research_mode,
                };

                onMessage(message);
              }
            }
          }
        }
      }
    } catch (error) {
      onError(error as Error);
    }
  },

  // 計画編集（編集クエリベース）
  editResearchPlan: async (uuid: string, request: EditPlanRequest) => {
    return apiRequest<EditPlanResponse>(`${BASE_URL}/${uuid}/edit-plan/`, "POST", request);
  },

  // 調査一覧取得
  getResearchList: async (params?: { page?: number; page_size?: number; status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
    if (params?.status) queryParams.append("status", params.status);

    const url = queryParams.toString() ? `${BASE_URL}/?${queryParams}` : `${BASE_URL}/`;

    return apiRequest<{
      count: number;
      next: string | null;
      previous: string | null;
      results: Research[];
    }>(url);
  },

  // 調査詳細取得
  getResearchDetail: async (uuid: string) => {
    return apiRequest<ResearchDetail>(`${BASE_URL}/${uuid}/`);
  },

  // 調査削除
  deleteResearch: async (uuid: string) => {
    return apiRequest(`${BASE_URL}/${uuid}/delete/`, "DELETE");
  },

  // 調査状況確認
  getResearchStatus: async (uuid: string) => {
    return apiRequest<{
      uuid: string;
      title: string;
      status: string;
      progress: number;
      created_at: string;
      completed_at: string | null;
      total_sources: number;
      error_message: string;
      message?: string;
      current_section?: number;
      search_count?: number;
      max_searches?: number;
    }>(`${BASE_URL}/${uuid}/status/`);
  },

  // 統計情報取得
  getResearchStats: async () => {
    return apiRequest<ResearchStats>(`${BASE_URL}/stats/`);
  },

  // レポートエクスポート
  exportResearch: async (uuid: string, request: ExportRequest) => {
    return apiRequest<ExportResponse>(`${BASE_URL}/${uuid}/export/`, "POST", request);
  },
};

// ヘルパー関数
export const downloadReport = (content: string, filename: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ステータス表示用のヘルパー（翻訳キーを返す）
export const getStatusLabel = (status: string): string => {
  const statusMap: Record<string, string> = {
    analyzing: "deepResearch.status.analyzing",
    planning: "deepResearch.status.planning",
    plan_pending: "deepResearch.status.planPending",
    researching: "deepResearch.status.researching",
    generating: "deepResearch.status.generating",
    completed: "deepResearch.status.completed",
    error: "deepResearch.status.error",
  };
  return statusMap[status] || status;
};

export const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    analyzing: "info",
    planning: "info",
    plan_pending: "warning",
    researching: "warning",
    generating: "info",
    completed: "success",
    error: "error",
  };
  return colorMap[status] || "default";
};

export const getScopeLabel = (scope: string): string => {
  const scopeMap: Record<string, string> = {
    narrow: "deepResearch.scope.narrow",
    medium: "deepResearch.scope.medium",
    broad: "deepResearch.scope.broad",
  };
  return scopeMap[scope] || scope;
};

export const getDepthLabel = (depth: string): string => {
  const depthMap: Record<string, string> = {
    surface: "deepResearch.depth.surface",
    detailed: "deepResearch.depth.detailed",
    expert: "deepResearch.depth.expert",
  };
  return depthMap[depth] || depth;
};

export const getReliabilityLabel = (reliability: string): string => {
  const reliabilityMap: Record<string, string> = {
    high: "deepResearch.reliability.high",
    medium: "deepResearch.reliability.medium",
    low: "deepResearch.reliability.low",
    unverified: "deepResearch.reliability.unverified",
  };
  return reliabilityMap[reliability] || reliability;
};

export const getReliabilityColor = (reliability: string): string => {
  const colorMap: Record<string, string> = {
    high: "success",
    medium: "warning",
    low: "error",
    unverified: "default",
  };
  return colorMap[reliability] || "default";
};
